"use client";

import {
  ArrowUpRight,
  Bed,
  Droplets,
  Coffee,
  Sparkles,
  SprayCanIcon,
} from "lucide-react";
import Link from "next/link";
import Image from "next/image";
import { cn } from "@/lib/utils";

interface ServiceItem {
  id: string;
  title: string;
  description: string;
  href: string;
  icon: React.ReactNode;
  image: string;
  price: string;
  features: string[];
}

const serviceItems: ServiceItem[] = [
  {
    id: "linen-hire",
    title: "Linen Hire",
    description:
      "Premium quality, high thread-count, fresh, and crisp white linen for the perfect guest experience.",
    href: "/linen-services",
    icon: <Bed className="w-5 h-5" />,
    image: "/assets/Images/sheets.avif",
    price: "From £11.95",
    features: ["Hotel-grade quality", "Wash & return", "Next-day delivery"],
  },
  {
    id: "towel-hire",
    title: "Towel Hire",
    description:
      "Soft, 100% cotton, fresh and conditioned high-quality white towels that guests love.",
    href: "/linen-services",
    icon: <Droplets className="w-5 h-5" />,
    image: "/assets/Images/towels.avif",
    price: "From £1.00",
    features: ["100% cotton", "Conditioned soft", "Eco-friendly wash"],
  },
  {
    id: "consumables",
    title: "Consumables",
    description:
      "Breakfast confectionary products including tea, coffee, and biscuits for a welcoming stay.",
    href: "/cleaning-guest-solutions",
    icon: <Coffee className="w-5 h-5" />,
    image: "/assets/Images/consumables.JPG",
    price: "From £3.00",
    features: ["Premium brands", "Custom packs", "Auto-replenish"],
  },
  {
    id: "toiletries",
    title: "Toiletries",
    description:
      "Bathroom replenishment of soap, shampoo, and toilet tissue for guest comfort.",
    href: "/cleaning-guest-solutions",
    icon: <SprayCanIcon className="w-5 h-5" />,
    image: "/assets/Images/bathroom.avif",
    price: "From £3.00",
    features: ["Guest-ready kits", "Quality brands", "Tracked by unit"],
  },
  {
    id: "cleaning",
    title: "Cleaning",
    description:
      "Full room service cleaning and replenishment of amenities for consistently high standards.",
    href: "/cleaning-services",
    icon: <Sparkles className="w-5 h-5" />,
    image: "/assets/Images/cleaning.JPG",
    price: "From £20.00",
    features: ["Verified standards", "Photo completion", "Same-day turnaround"],
  },
];

interface ServicesGridProps {
  className?: string;
}

export function ServicesGrid({ className }: ServicesGridProps) {
  return (
    <section className={cn("w-full", className)}>
      {/* Header */}
      <div className="max-w-3xl mb-10 lg:mb-14">
        <span className="inline-block text-xs font-bold uppercase tracking-widest text-teal-600 mb-3">
          Services
        </span>
        <h2 className="text-xl sm:text-2xl lg:text-3xl font-satoshi font-bold text-gray-900 leading-tight mb-4">
          Host and property turnover services
        </h2>
        <p className="text-sm sm:text-base font-light text-gray-500 leading-relaxed">
          Linen hire, towel hire, cleaning, and consumables delivered through one
          system. Book, track, and verify every turnover with live updates and
          portfolio-wide visibility.
        </p>
      </div>

      {/* Top row: 2 featured cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-5 mb-4 lg:mb-5">
        {serviceItems.slice(0, 2).map((item) => (
          <ServiceCardFeatured key={item.id} item={item} />
        ))}
      </div>

      {/* Bottom row: 3 compact cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5">
        {serviceItems.slice(2).map((item) => (
          <ServiceCardCompact key={item.id} item={item} />
        ))}
      </div>
    </section>
  );
}

function ServiceCardFeatured({ item }: { item: ServiceItem }) {
  return (
    <Link
      href={item.href}
      className="group relative flex flex-col sm:flex-row rounded-2xl bg-white border border-gray-100/80 overflow-hidden hover:shadow-lg hover:border-gray-200/80 transition-all duration-300"
    >
      {/* Image */}
      <div className="relative sm:w-2/5 aspect-[4/3] sm:aspect-auto overflow-hidden bg-gray-50 flex-shrink-0">
        <Image
          src={item.image}
          alt={item.title}
          fill
          className="object-cover transition-transform duration-500 group-hover:scale-105"
          sizes="(max-width: 640px) 100vw, (max-width: 768px) 50vw, 40vw"
        />
        <div className="absolute inset-0 bg-gradient-to-t sm:bg-gradient-to-r from-black/20 via-transparent to-transparent" />
      </div>

      {/* Content */}
      <div className="flex flex-col flex-1 p-5 sm:p-6">
        <div className="flex items-center gap-2.5 mb-3">
          <div className="w-8 h-8 rounded-lg bg-gray-900 flex items-center justify-center text-white flex-shrink-0">
            {item.icon}
          </div>
          <h3 className="text-base font-semibold text-gray-900 group-hover:text-gray-700 transition-colors">
            {item.title}
          </h3>
        </div>

        <p className="text-sm font-normal text-gray-400 leading-relaxed mb-4">
          {item.description}
        </p>

        {/* Features */}
        <div className="flex flex-wrap gap-1.5 mb-4">
          {item.features.map((feature, idx) => (
            <span
              key={idx}
              className="text-[11px] font-normal text-gray-500 bg-gray-50 px-2.5 py-1 rounded-full border border-gray-100"
            >
              {feature}
            </span>
          ))}
        </div>

        {/* Footer */}
        <div className="mt-auto flex items-center justify-between pt-3 border-t border-gray-50">
          <span className="text-xs font-medium text-gray-500">
            {item.price}
          </span>
          <div className="flex items-center gap-1.5 text-sm font-medium text-gray-900 group-hover:text-gray-600 transition-colors">
            Learn more
            <ArrowUpRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </div>
        </div>
      </div>
    </Link>
  );
}

function ServiceCardCompact({ item }: { item: ServiceItem }) {
  return (
    <Link
      href={item.href}
      className="group flex flex-col rounded-2xl bg-white border border-gray-100/80 overflow-hidden hover:shadow-lg hover:border-gray-200/80 transition-all duration-300"
    >
      {/* Image */}
      <div className="relative aspect-[16/9] overflow-hidden bg-gray-50">
        <Image
          src={item.image}
          alt={item.title}
          fill
          className="object-cover transition-transform duration-500 group-hover:scale-105"
          sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />
        <div className="absolute bottom-3 right-3">
          <span className="text-xs font-medium text-white bg-black/40 backdrop-blur-sm px-3 py-1.5 rounded-full">
            {item.price}
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="flex flex-col flex-1 p-5">
        <div className="flex items-center gap-2.5 mb-3">
          <div className="w-8 h-8 rounded-lg bg-gray-900 flex items-center justify-center text-white flex-shrink-0">
            {item.icon}
          </div>
          <h3 className="text-base font-semibold text-gray-900 group-hover:text-gray-700 transition-colors">
            {item.title}
          </h3>
        </div>

        <p className="text-sm font-normal text-gray-400 leading-relaxed line-clamp-2 mb-4">
          {item.description}
        </p>

        {/* Features */}
        <div className="flex flex-wrap gap-1.5 mb-4">
          {item.features.map((feature, idx) => (
            <span
              key={idx}
              className="text-[11px] font-normal text-gray-500 bg-gray-50 px-2.5 py-1 rounded-full border border-gray-100"
            >
              {feature}
            </span>
          ))}
        </div>

        {/* CTA */}
        <div className="mt-auto pt-3 border-t border-gray-50">
          <div className="flex items-center gap-1.5 text-sm font-medium text-gray-900 group-hover:text-gray-600 transition-colors">
            Learn more
            <ArrowUpRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </div>
        </div>
      </div>
    </Link>
  );
}
